import React, { useState, useEffect } from 'react';
import apiClient from '../apiClient';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useShop } from '../context/ShopContext';
import { fetchSortOrderMap, applySortOrder, buildCategorySegments } from '../utils/sortOrder';

/* ─── PDF section info cards ─────────────────────────── */
const SECTIONS = [
  { icon: '🍾', label: 'English Record', sub: 'Opening · Received · Transferred · Sales · Amount', color: '#3B82F6' },
  { icon: '🍶', label: 'Desi Record',    sub: 'Opening · Received · Transferred · Sales · Amount', color: '#10B981' },
  { icon: '📊', label: 'Net Sale',       sub: 'English · Desi · Expense · Debit · Credit · Net',  color: '#8B5CF6' },
];

const PrintSlip = () => {
  const [stockDate, setStockDate] = useState('');
  const [loading, setLoading] = useState(false);
  const { shopName, shopId, companyName } = useShop();

  useEffect(() => {
    setStockDate(new Date().toISOString().split('T')[0]);
  }, []);

  /* ─── PDF helpers ─────────────────────────────────── */
  const drawHeader = (doc, { shopName, licenseNo, slipName, date }) => {
    let formattedDate = date;
    if (date.includes('-')) {
      const [day, month, year] = date.split('-');
      formattedDate = `${Number(day)}-${Number(month)}-${year}`;
    }
    autoTable(doc, {
      margin: { top: 14, left: 14, right: 14 },
      startY: 14,
      head: [[
        { content: `Shop Name: ${shopName || 'N/A'}`, styles: { halign: 'left' } },
        { content: `Purcha: ${slipName}`,             styles: { halign: 'center' } },
        { content: `SalesmanName: ${licenseNo || 'N/A'}`, styles: { halign: 'center' } },
        { content: `Date: ${formattedDate}`,          styles: { halign: 'right' } },
      ]],
      styles: { fontSize: 10, cellPadding: 2, lineColor: [0,0,0], lineWidth: 0.5, textColor: [0,0,0] },
      headStyles: { fillColor: [255,255,255], textColor: [0,0,0], fontStyle: 'bold' },
      tableLineWidth: 0.5,
      tableLineColor: [0,0,0],
    });
  };

  const calculateTotals = (data, keys) =>
    keys.map(key => data.reduce((sum, row) => sum + Number(row[key] || 0), 0));

  const generatePDF = async () => {
    setLoading(true);
    try {
      const { data: resp } = await apiClient.post(
        '/Liquor/GetPrintSlipData',
        { stockDate: new Date(stockDate).toISOString(), shopId }
      );

      const rawEnglish = resp.data?.productEnglishSummaries || [];
      const rawDesi    = resp.data?.productDesiSummaries    || [];
      const sales      = resp.data?.productSalesResponses   || [];

      // Re-sort by the authoritative shop+type sort-order map so the printed sequence
      // matches every other parcha rendering surface (screens use typeId "1"/"2" too).
      const [englishSortMap, desiSortMap] = await Promise.all([
        fetchSortOrderMap(shopId, "1"),
        fetchSortOrderMap(shopId, "2"),
      ]);
      const english = applySortOrder(rawEnglish, englishSortMap);
      const desi    = applySortOrder(rawDesi, desiSortMap);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const dateObj = new Date(stockDate);
      const formattedDate = `${dateObj.getDate()}-${dateObj.getMonth() + 1}-${dateObj.getFullYear()}`;
      const startY  = 30;
      const margin  = 14;

      const productColumns  = ['S.No.', 'Product Name', 'Variant', 'Opening Stock', 'Received', 'Transferred', 'Total', 'Closing Stock', 'Sales', 'Rate', 'Amount', 'RD Debit', 'RD Credit'];
      const productTotalKeys = ['openingStock', 'rece', 'trans', 'Total', 'closingStock', 'sales', 'mrp', 'amount', 'rdDebit', 'rdCredit'];

      // Prints a category header only where the (already sort-order-sorted) sequence's
      // category actually changes, instead of gathering every row of a category
      // together and alphabetically re-sorting categories/products — that would undo
      // the admin's interleaved Global Sort Order.
      const groupByCategory = (data) =>
        buildCategorySegments(data, (item) => item.categoryName || item.catName || item.category || 'General')
          .map((seg) => ({ cat: seg.cat, items: seg.rows }));

      const renderTableWithFooter = (doc, { title, data, columns, startY, shopName, licenseNo, date }) => {
        drawHeader(doc, { shopName, slipName: title, licenseNo, date });
        const dataTableStartY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 0.5 : startY;

        if (data.length) {
          const groups = groupByCategory(data);
          let sno = 1;
          const body = [];
          const grand = { openingStock: 0, rece: 0, trans: 0, closingStock: 0, sales: 0, amount: 0, rdDebit: 0, rdCredit: 0 };

          groups.forEach(({ cat, items }) => {
            body.push([{
              content: `  ${cat.toUpperCase()}`,
              colSpan: 13,
              styles: { fillColor: [61, 13, 38], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'left', fontSize: 8, cellPadding: { top: 3, bottom: 3, left: 8, right: 6 } },
            }]);

            const catT = { openingStock: 0, rece: 0, trans: 0, closingStock: 0, sales: 0, amount: 0, rdDebit: 0, rdCredit: 0 };
            items.forEach(i => {
              body.push([sno++, i.pName, i.packing, i.openingStock, i.rece, i.trans, (i.openingStock + i.rece - i.trans), i.closingStock, i.sales, i.mrp, i.amount, i.rdDebit, i.rdCredit]);
              Object.keys(catT).forEach(k => { catT[k] += Number(i[k]) || 0; });
            });
            Object.keys(grand).forEach(k => { grand[k] += catT[k]; });

            const cs = { fontStyle: 'bold', fillColor: [196, 126, 160], textColor: [255, 255, 255] };
            body.push([
              { content: `${cat} Subtotal`, colSpan: 3, styles: { ...cs, halign: 'right' } },
              { content: catT.openingStock.toFixed(2), styles: cs },
              { content: catT.rece.toFixed(2), styles: cs },
              { content: catT.trans.toFixed(2), styles: cs },
              { content: (catT.openingStock + catT.rece - catT.trans).toFixed(2), styles: cs },
              { content: catT.closingStock.toFixed(2), styles: cs },
              { content: catT.sales.toFixed(2), styles: cs },
              { content: '', styles: cs },
              { content: catT.amount.toFixed(2), styles: cs },
              { content: catT.rdDebit.toFixed(2), styles: cs },
              { content: catT.rdCredit.toFixed(2), styles: cs },
            ]);
          });

          const fs = { fontStyle: 'bold' };
          const footRow = [
            { content: 'Grand Total', colSpan: 3, styles: { ...fs, halign: 'right' } },
            { content: grand.openingStock.toFixed(2), styles: fs },
            { content: grand.rece.toFixed(2), styles: fs },
            { content: grand.trans.toFixed(2), styles: fs },
            { content: (grand.openingStock + grand.rece - grand.trans).toFixed(2), styles: fs },
            { content: grand.closingStock.toFixed(2), styles: fs },
            { content: grand.sales.toFixed(2), styles: fs },
            { content: '', styles: fs },
            { content: grand.amount.toFixed(2), styles: fs },
            { content: grand.rdDebit.toFixed(2), styles: fs },
            { content: grand.rdCredit.toFixed(2), styles: fs },
          ];

          autoTable(doc, {
            head: [columns],
            body,
            foot: [footRow],
            showFoot: 'lastPage',
            startY: dataTableStartY,
            styles: { fontSize: 7, cellPadding: 1.5, halign: 'center', lineColor: '#000', lineWidth: 0.5 },
            headStyles: { fillColor: [0, 80, 255], textColor: '#fff', fontStyle: 'bold' },
            bodyStyles: { fontStyle: 'bold', textColor: '#000' },
            footStyles: { fillColor: [230, 230, 230], textColor: '#000', fontStyle: 'bold' },
          });
        } else {
          doc.setFontSize(12).text('No records found.', margin, dataTableStartY);
        }
      };

      const renderSalesTable = (doc, data, startY) => {
        drawHeader(doc, { shopName, slipName: 'Net Sale', licenseNo: companyName, date: formattedDate });
        const dataTableStartY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 0.5 : startY;

        if (data.length) {
          const footTotals = calculateTotals(data, ['english','desi','sales','exp','debit','credit','salary','commissionValue','amount','rdDebit','rdCredit']);
          const footRow = [
            { content: 'Total', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
            ...footTotals.map(val => ({ content: val.toFixed(2), styles: { fontStyle: 'bold' } })),
          ];
          autoTable(doc, {
            head: [['Shop Name','Date','English','Desi','Total Sales','Total Expense','Debit','Credit','Salary','Commission','Total Net Sales','RD Debit','RD Credit']],
            body: data.map(i => [
              i.shopName,
              i.salesDate?.split('T')[0]?.split('-').reverse().join('-') || '',
              i.english, i.desi, i.sales, i.exp, i.debit, i.credit,
              i.salary, i.commissionValue, i.amount, i.rdDebit, i.rdCredit,
            ]),
            foot: [footRow],
            showFoot: 'lastPage',
            startY: dataTableStartY,
            styles: { fontSize: 7, cellPadding: 1.5, halign: 'center', lineColor: '#000', lineWidth: 0.5 },
            headStyles: { fillColor: [0,123,255], textColor: '#fff', fontStyle: 'bold' },
            bodyStyles: { fontStyle: 'bold', textColor: '#000' },
            footStyles: { fillColor: [230,230,230], textColor: '#000', fontStyle: 'bold' },
          });
        } else {
          doc.setFontSize(12).text('No records found.', margin, dataTableStartY);
        }
      };

      renderTableWithFooter(doc, { title: 'English Record', data: english, columns: productColumns, startY, shopName, licenseNo: companyName, date: formattedDate });
      doc.addPage('a4', 'landscape');
      renderTableWithFooter(doc, { title: 'Desi Record',    data: desi,    columns: productColumns, startY, shopName, licenseNo: companyName, date: formattedDate });
      doc.addPage('a4', 'landscape');
      renderSalesTable(doc, sales, startY);

      const dateStr = new Date(stockDate).toLocaleDateString('en-GB').replace(/\//g, '-');
      doc.save(`Liquor-PrintSlip(${dateStr}).pdf`);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      console.error('PDF Generation Error:', err);
      toast.error('Error generating PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!stockDate) { toast.error('Please select a stock date.'); return; }
    generatePDF();
  };

  const displayDate = stockDate
    ? new Date(stockDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  /* ─── UI ──────────────────────────────────────────── */
  return (
    <div>
      <ToastContainer />

      <div style={{ padding: '28px 28px 36px' }}>

        {/* ── Page description ── */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
            Select a date to generate and download the complete daily parcha (print slip) as a
            landscape PDF. The PDF contains three sections — English record, Desi record, and Net Sale summary.
          </p>
        </div>

        {/* ── Section info cards ── */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 32 }}>
          {SECTIONS.map((s, i) => (
            <div key={i} style={{
              flex: '1 1 180px',
              background: '#fff',
              border: `1.5px solid ${s.color}22`,
              borderLeft: `4px solid ${s.color}`,
              borderRadius: 12,
              padding: '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 12,
              boxShadow: `0 2px 8px ${s.color}12`,
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                background: s.color + '15',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
              }}>{s.icon}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', marginBottom: 3 }}>
                  Page {i + 1} — {s.label}
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.5 }}>{s.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Date selection + download card ── */}
        <form onSubmit={handleSubmit}>
          <div style={{
            background: 'linear-gradient(135deg, #F8FAFF 0%, #F0F4FF 100%)',
            border: '1.5px solid rgba(99,102,241,0.14)',
            borderRadius: 14,
            padding: '24px 24px 20px',
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 16 }}>📅</span> Select Date
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>

              {/* Date input */}
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6366F1', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Stock Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={stockDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => setStockDate(e.target.value)}
                  style={{ fontSize: 14, fontWeight: 600 }}
                />
                {displayDate && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#6B7280' }}>
                    📆 {displayDate}
                  </div>
                )}
              </div>

              {/* Download button */}
              <div style={{ flex: '0 0 auto' }}>
                <button
                  type="submit"
                  disabled={loading || !stockDate}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #4F46E5, #6366F1)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '12px 28px',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: loading ? 'none' : '0 4px 14px rgba(99,102,241,0.35)',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                    minWidth: 180,
                    justifyContent: 'center',
                  }}
                >
                  {loading ? (
                    <>
                      <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                      Generating PDF…
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 17 }}>⬇️</span>
                      Download Parcha PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* ── What's inside note ── */}
        <div style={{
          marginTop: 24,
          background: '#FFFBEB',
          border: '1px solid #FDE68A',
          borderLeft: '4px solid #F59E0B',
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>💡</span>
          <div style={{ fontSize: 12, color: '#92400E', lineHeight: 1.65 }}>
            <strong>Landscape A4 PDF</strong> — The downloaded file has 3 pages in landscape orientation.
            Columns include: <em>Opening, Received, Transferred, Total, Closing, Sales, Rate, Amount, RD Debit, RD Credit</em>.
            A totals row is printed at the bottom of each section.
          </div>
        </div>

      </div>

      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.55)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 18, backdropFilter: 'blur(4px)',
        }}>
          <div style={{ width: 54, height: 54, border: '4px solid rgba(255,255,255,0.15)', borderTopColor: '#6366F1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Generating PDF…</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>Fetching data and building landscape pages</div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};

export default PrintSlip;
